import { IExtendedMetaAction } from './useSalesOrderPostSave';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ModalContext from '../../../components/Context/ModalContext';
import { alternateFullScreenModalStyle } from '../../../components/modals/Styles';
import {
  AlertType,
  IMetaAction,
  ISalesOrderDelayedPostSaveMutationResult,
  ISalesOrderDelayedPostSaveMutationVariables,
  ISalesOrderInput,
  Maybe,
} from '../../../graphql/schema/p4types';
import PurchaseRequirementDetailPreviewWorkspace from '../../purchases/PurchaseRequirementDetailPreviewWorkspace';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { IJobMeta, useBackgroundMutation } from '../../../utils/BackgroundJobs';
import SalesOrderDelayedPostSaveMutation from '../../../graphql/mutations/sales/SalesOrderDelayedPostSaveMutation.graphql';
import { useP4ToastController } from '../../system/NotificationsArea';
import FluentModal from '../../../components/modals/FluentModal';

const AssemblyShortageModal = ({
  modalOpen,
  metaAction,
  addActions,
  doDelayedPostSave = true,
}: {
  modalOpen: boolean;
  metaAction?: any;
  addActions?: (actions: IExtendedMetaAction[]) => void;
  doDelayedPostSave?: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const { values: unTypedValues, match } = connectedForm;
  const { onClose } = useContext(ModalContext);
  const [isOpen, setIsOpen] = useState(modalOpen);
  const values: Maybe<ISalesOrderInput> = unTypedValues;
  const { dispatchSimpleToast } = useP4ToastController();

  useEffect(() => {
    //'modalOpen' prop is source-of-truth, however we want to "minimize" the modal while the background job runs before we call props.close, to ensure data is all in-sync as expected.
    setIsOpen(modalOpen);
  }, [modalOpen]);

  const jobFilter = [
    {
      filterOperation: 'EQUALS',
      filterParameters: {
        field: 'jobId',
        value: metaAction?.jobInfo?.jobId,
      },
    },
  ];

  //checks whether the given object is of type IMetaAction
  const isIMetaAction = (object: any): object is IMetaAction => {
    return 'actionType' in object;
  };

  //Checks whether the given object is an array of IMetaAction
  const isIMetaActionArr = (object: any): object is IMetaAction[] => {
    const isArray = Array.isArray(object);
    if (isArray && object.length > 0) {
      //check that all items are of type IMetaAction
      return object.every(isIMetaAction);
    }
    return true;
  };

  //extracts the meta actions from a JobInfo metadata
  const extractMetaActions = (jobInfo: IJobMeta): void => {
    //process any new actions
    const jobInfoProperties = jobInfo.properties.metadata;
    if (addActions && jobInfoProperties) {
      try {
        const metadata = JSON.parse(jobInfoProperties);
        //extract the actions
        const actions: IExtendedMetaAction[] = [];
        if (isIMetaActionArr(metadata)) {
          for (const entry of metadata) {
            const action: IExtendedMetaAction = {
              __typename: 'MetaAction',
              actionType: entry.actionType,
              jobInfo: 'jobInfo' in entry ? entry.jobInfo : null,
              postSave: true,
            };
            actions.push(action);
          }
        }
        if (actions.length > 0) {
          addActions(actions);
        }
      } catch (exception) {
        console.error(
          'Problem parsing metadata, could not parse the supplied metadata on the job meaning there could be a ' +
            'problem showing the post order modals e.g. picking list'
        );
        dispatchSimpleToast(
          'Problem processing post sales events',
          'Some post sales events may not display correctly.',
          AlertType.Warning
        );
      }
    }
  };

  const [salesOrderDelayedPostSave] = useBackgroundMutation<
    ISalesOrderDelayedPostSaveMutationResult,
    ISalesOrderDelayedPostSaveMutationVariables
  >(SalesOrderDelayedPostSaveMutation, {
    onCompleteJob: (jobInfo: IJobMeta) => {
      extractMetaActions(jobInfo);
      //Call modal close to trigger the next modal or navigation as appropriate.
      onClose?.();
      //Trigger a concurrency reload to update concurrencyVersion and any other relevant fields, so if the user doesn't print any documents (Which would also call concurrencyReloadData),
      //then the form is still brought up to date for further editing.
      connectedForm.concurrencyReloadData?.();
    },
  });

  /**
   * Close the shortage modal & trigger delayed post-save logic, including tagging the lines as having had shortages processed.
   */
  const onCloseModal = useCallback(() => {
    const variables: ISalesOrderDelayedPostSaveMutationVariables = {
      input: {
        clientMutationId: Math.random().toString(36).substring(2, 32),
        salesOrderId: values?.id,
      },
    };

    //Delayed post save will trigger automatic allocations if configured to do so.
    if (doDelayedPostSave) {
      salesOrderDelayedPostSave({ variables: variables });
    } else {
      onClose?.();
    }
    setIsOpen(false);
  }, [salesOrderDelayedPostSave, values]);

  const modalProviderValue = useMemo(
    () => ({ onClose: onCloseModal, style: alternateFullScreenModalStyle }),
    [onCloseModal]
  );

  return (
    <ModalContext.Provider value={modalProviderValue}>
      <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '1000px' } }}>
        <PurchaseRequirementDetailPreviewWorkspace
          embeddedView={true}
          includeHeader={true}
          disableViewController={true}
          viewScreenName="/Purchasing/Shortages/ShortageDetailPreview/Modal"
          removeAddButton={false}
          predefinedFilters={jobFilter}
          salesOrderNumber={values?.orderNumber}
          customerName={values?.customer?.name}
          history={history}
          match={match}
          assemblyOptions={true}
        />
      </FluentModal>
    </ModalContext.Provider>
  );
};

export default AssemblyShortageModal;
